<template>
    <md-card :class="{ 'open-item': showContent }">
        <div v-if="isLoadingImage"
            class="spinner-area">
            <md-progress-spinner md-mode="indeterminate">
            </md-progress-spinner>
        </div>
        <div v-else>
            <a @click="showContent = !showContent">
                <!-- IMAGE -->
                <md-card-media-cover md-text-scrim>
                    <md-card-media md-ratio="16:9">
                        <img :src="product.picture_b64"
                            :alt="product.picture_name">
                    </md-card-media>
                    <md-card-area>
                        <span>{{ $t('orders.make-order') }}</span>
                    </md-card-area>
                </md-card-media-cover>
                <!-- HEADER -->
                <md-card-area>
                    <md-card-header>
                        <div class="md-title">
                            <h4>{{ product.product_name }}</h4>
                        </div>
                    </md-card-header>
                </md-card-area>
            </a>
            <!-- CONTENT -->
            <md-card-content>
                <div v-if="showContent"
                    class="md-layout md-gutter">
                    <div class="md-layout-item md-size-50">
                        <md-field :class="{'md-invalid': $v.order.quantity.$dirty && $v.order.quantity.$invalid}">
                            <label>
                                {{ $tc('orders.quantity', 1) }}
                            </label>
                            <md-input v-model="order.quantity"
                                type="number"
                                min="1"
                                @input="$v.order.quantity.$touch()">
                            </md-input>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-size-50">
                        <md-field>
                            <label>
                                {{ $tc('orders.unit', 1) }}
                            </label>
                            <md-select v-model="order.units"
                                name="description"
                                id="unit_id"
                                md-dense
                                :disabled="units.length < 1"
                                @input="$v.order.units.$touch()">
                                <md-option v-for="unit in units"
                                    :key="unit.unit_id"
                                    :value="unit.description">
                                    {{ unit.description }}
                                </md-option>
                            </md-select>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-size-100">
                        <md-field>
                            <label>
                                {{ $tc('orders.caliber', 1) }}
                            </label>
                            <md-select v-model="order.caliber"
                                name="description"
                                id="caliber_id"
                                md-dense
                                :disabled="calibers.length < 1"
                                @input="$v.order.caliber.$touch()">
                                <md-option v-for="caliber in calibers"
                                    :key="caliber.caliber_id"
                                    :value="caliber.description">
                                    {{ caliber.description }}
                                </md-option>
                            </md-select>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-size-100">
                        <md-field>
                            <label>
                                {{ $t('orders.fish-status') }}
                            </label>
                            <md-select v-model="order.product_status"
                                name="description"
                                id="product_status_id"
                                md-dense
                                :disabled="productStatus.length < 1"
                                @input="$v.order.product_status.$touch()">
                                <md-option v-for="status in productStatus"
                                    :key="status.product_status_id"
                                    :value="status.description">
                                    {{ status.description }}
                                </md-option>
                            </md-select>
                        </md-field>
                    </div>
                    <label class="label-switch md-layout-item md-size-66">
                        {{ $t('orders.preparation_question') }}
                    </label>
                    <md-switch v-model="order.prepared"
                        :value="true"
                        @change="value => updatePrepared(value)"
                        class="md-primary md-layout-item md-size-33">
                    </md-switch>
                    <md-button class="md-raised md-primary"
                        :disabled="$v.$invalid"
                        @click="addToCart">
                        <md-icon>add_shopping_cart</md-icon>
                        {{ $t('orders.add-to-cart') }}
                    </md-button>
                </div>
            </md-card-content>
        </div>
    </md-card>
</template>

<script>
import _ from 'lodash'
import productsService from '@/components/products/services/'
import Utils from '@/utils/utils'
import { required, decimal } from 'vuelidate/lib/validators'
import OrderStatus from '@/utils/orderStatus'

export default {
    name: 'ProductCard',

    props: {
        product: {
            type: Object,
            default: null
        },

        units: {
            type: Array,
            default: null
        },

        productStatus: {
            type: Array,
            default: null
        }
    },

    data () {
        return {
            calibers: [],

            showContent: false,

            order: {
                product_id: this.product.product_id,
                product_name: this.product.product_name,
                picture_b64: null,
                picture_url: this.product.picture_url,
                picture_name: this.product.picture_name,
                category_id: this.product.category_id,
                category_name: this.product.category_name,
                scientific_name: this.product.scientific_name,
                quantity: null,
                units: null,
                caliber: null,
                prepared: false,
                product_status: null,
                notes: null,
                active: 1,
                order_item_status_id: OrderStatus.orderItem.PENDING,
                selected: true
            },

            isLoadingImage: false
        }
    },

    validations: {
        order: {
            quantity: {
                required,
                decimal
            },
            units: {
                required
            },
            caliber: {
                required
            },
            product_status: {
                required
            }
        }
    },

    methods: {
        addToCart () {
            this.showContent = !this.showContent

            this.$store.dispatch('addToCart', this.order)
            this.cleanCard()
        },

        updatePrepared (value) {
            let self = this
            if (value === null) {
                self.order.prepared = false
            } else {
                self.order.prepared = true
            }
        },

        async fetchProductCalibers () {
            let self = this

            let params = {
                filter: {
                    product_id: self.product.product_id
                }
            }

            let response = await productsService.getProductCalibers(params)
            self.calibers = _.cloneDeep(response.data)
        },

        async fetchImage () {
            if (this.product) {
                this.isLoadingImage = true
                this.order.picture_b64 = this.product.picture_b64 = await Utils.getBase64(this.product.picture_url)
                this.isLoadingImage = false
            }
        },

        cleanCard () {
            this.order = {
                product_id: this.product.product_id,
                product_name: this.product.product_name,
                picture_b64: this.product.picture_b64,
                picture_url: this.product.picture_url,
                picture_name: this.product.picture_name,
                category_id: this.product.category_id,
                category_name: this.product.category_name,
                scientific_name: this.product.scientific_name,
                quantity: null,
                units: null,
                caliber: null,
                prepared: 0,
                product_status: null,
                notes: null,
                active: 1,
                order_item_status_id: OrderStatus.orderItem.PENDING,
                selected: true
            }
            this.$v.$reset()
        }
    },

    created () {
        this.fetchImage()
        this.fetchProductCalibers()
    }
}
</script>
